<template>
  <v-container fluid class="intelligence-skills-gap pa-4">
    <v-row>
      <v-col cols="12">
        <h1 class="text-h5 font-weight-bold mb-1 theme-heading">
          Skills Gap Analysis
        </h1>
        <p class="text-subtitle-2 theme-text-secondary mb-2">
          Org-wide skill landscape with drill-down to individual candidate analysis
        </p>
      </v-col>
    </v-row>

    <v-alert v-if="hasError" type="error" dismissible class="mb-4">
      {{ errorMessage }}
    </v-alert>

    <v-row class="mb-2">
      <v-col cols="12">
        <v-btn-toggle v-model="activeView" mandatory dense color="#007AFF" class="mb-2">
          <v-btn value="heatmap" small>
            <v-icon small left>mdi-grid</v-icon>
            Org Skills Heatmap
          </v-btn>
          <v-btn value="person" small>
            <v-icon small left>mdi-account-search</v-icon>
            Individual Analysis
          </v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <!-- ORG-LEVEL HEATMAP VIEW -->
    <div v-if="activeView === 'heatmap'">
      <v-progress-linear v-if="isLoadingHeatMap" indeterminate color="#007AFF" class="mb-4" />

      <!-- Summary cards -->
      <v-row dense class="mb-4" v-if="heatMapSummary.totalProfiles">
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-3 text-center">
            <div class="text-h4 font-weight-bold" style="color: #007AFF">
              {{ heatMapSummary.totalProfiles }}
            </div>
            <div class="text-caption grey--text">Profiles Analyzed</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-3 text-center">
            <div class="text-h4 font-weight-bold" style="color: #10B981">
              {{ heatMapSummary.totalUniqueSkills }}
            </div>
            <div class="text-caption grey--text">Unique Skills</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-3 text-center">
            <div class="text-h4 font-weight-bold" style="color: #8B5CF6">
              {{ heatMapSummary.functionalAreaCount }}
            </div>
            <div class="text-caption grey--text">Functional Areas</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card outlined class="pa-3 text-center">
            <div class="text-h5 font-weight-bold" style="color: #F59E0B">
              {{ heatMapSummary.topSkillOrg }}
            </div>
            <div class="text-caption grey--text">Most Common Skill ({{ heatMapSummary.topSkillOrgPct }}%)</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- SKILLS x FUNCTIONAL AREA HEATMAP -->
      <v-card outlined class="pa-4 mb-4" v-if="heatMapRows.length > 0">
        <h3 class="text-subtitle-2 font-weight-bold mb-1 theme-heading">
          <v-icon small color="#F59E0B" class="mr-1">mdi-fire</v-icon>
          Skills by Functional Area
        </h3>
        <p class="text-caption grey--text mb-3">
          Red cells highlight skill gaps; blue cells show areas of strength. Color intensity reflects the percentage of people in each area with this skill.
        </p>
        <div class="heatmap-container" style="overflow-x: auto">
          <table class="heatmap-table">
            <thead>
              <tr>
                <th class="heatmap-header-skill">Skill</th>
                <th class="heatmap-header-total">Org %</th>
                <th
                  v-for="area in heatMapAreas"
                  :key="area"
                  class="heatmap-header-area"
                >
                  {{ areaShort(area) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in heatMapRows.slice(0, 20)" :key="row.skill">
                <td class="heatmap-skill-name">{{ row.skill }}</td>
                <td class="heatmap-total">
                  <span class="font-weight-bold">{{ row.totalPercentage }}%</span>
                </td>
                <td
                  v-for="cell in row.cells"
                  :key="cell.area"
                  class="heatmap-cell"
                  :style="heatCellStyle(cell.percentage)"
                  :title="`${row.skill} in ${cell.area}: ${cell.count} people (${cell.percentage}%)`"
                >
                  <span v-if="cell.count > 0">{{ cell.count }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex align-center mt-3">
          <span class="text-caption grey--text mr-2">Coverage:</span>
          <div class="d-flex align-center">
            <div class="heatmap-legend-block" style="background: rgba(239,83,80,0.18); border: 1px dashed rgba(239,83,80,0.45)"></div>
            <span class="text-caption mx-1">Gap</span>
            <div class="heatmap-legend-block" style="background: rgba(150,150,150,0.08)"></div>
            <div class="heatmap-legend-block" style="background: rgba(0,122,255,0.1)"></div>
            <div class="heatmap-legend-block" style="background: rgba(0,122,255,0.3)"></div>
            <div class="heatmap-legend-block" style="background: rgba(0,122,255,0.55)"></div>
            <div class="heatmap-legend-block" style="background: rgba(0,122,255,0.8)"></div>
            <span class="text-caption ml-1">Strong</span>
          </div>
        </div>
      </v-card>

      <!-- SKILLS x SENIORITY HEATMAP -->
      <v-card outlined class="pa-4 mb-4" v-if="seniorityRows.length > 0">
        <h3 class="text-subtitle-2 font-weight-bold mb-1 theme-heading">
          <v-icon small color="#8B5CF6" class="mr-1">mdi-chart-timeline-variant-shimmer</v-icon>
          Skills by Seniority Level
        </h3>
        <p class="text-caption grey--text mb-3">
          See how skill concentrations shift across seniority levels. Gaps at senior levels may signal succession risk.
        </p>
        <div class="heatmap-container" style="overflow-x: auto">
          <table class="heatmap-table">
            <thead>
              <tr>
                <th class="heatmap-header-skill">Skill</th>
                <th
                  v-for="sen in heatMapSeniorities"
                  :key="sen"
                  class="heatmap-header-area"
                >
                  {{ sen }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in seniorityRows" :key="row.skill">
                <td class="heatmap-skill-name">{{ row.skill }}</td>
                <td
                  v-for="cell in row.cells"
                  :key="cell.seniority"
                  class="heatmap-cell"
                  :style="heatCellStyle(cell.percentage)"
                  :title="`${row.skill} at ${cell.seniority}: ${cell.count} people (${cell.percentage}%)`"
                >
                  <span v-if="cell.count > 0">{{ cell.count }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>

      <!-- FUNCTIONAL AREA CONCENTRATION -->
      <v-row dense class="mb-4" v-if="areaConcentration.length > 0">
        <v-col
          v-for="area in areaConcentration"
          :key="area.area"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <v-card outlined class="pa-3" style="height: 100%">
            <div class="d-flex align-center mb-2">
              <v-icon small color="#007AFF" class="mr-1">mdi-domain</v-icon>
              <span class="text-subtitle-2 font-weight-bold theme-heading">{{ area.area }}</span>
              <v-spacer />
              <v-chip x-small color="#E3F2FD" text-color="#007AFF">{{ area.profileCount }}</v-chip>
            </div>
            <div v-for="ts in area.topSkills" :key="ts.skill" class="mb-1">
              <div class="d-flex align-center justify-space-between">
                <span class="text-caption">{{ ts.skill }}</span>
                <span class="text-caption font-weight-bold">{{ ts.percentage }}%</span>
              </div>
              <v-progress-linear
                :value="ts.percentage"
                color="#007AFF"
                height="4"
                rounded
              />
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- SINGLE-POINT VULNERABILITIES -->
      <v-card outlined class="pa-4 mb-4" v-if="rarestSkills.length > 0">
        <h3 class="text-subtitle-2 font-weight-bold mb-1 theme-heading">
          <v-icon small color="#EF4444" class="mr-1">mdi-alert</v-icon>
          Single-Point Vulnerabilities
        </h3>
        <p class="text-caption grey--text mb-3">
          Skills held by 3 or fewer people. If any of them leave, this capability is at risk.
        </p>
        <v-row dense>
          <v-col
            v-for="(vuln, vi) in rarestSkills"
            :key="'vuln-' + vi"
            cols="12"
            sm="6"
            md="4"
          >
            <div class="vuln-card pa-2 mb-2" :class="(vuln.criticality || 'warning') === 'critical' ? 'vuln-critical' : 'vuln-warning'">
              <div class="d-flex align-center mb-1">
                <v-icon x-small :color="(vuln.criticality || 'warning') === 'critical' ? '#EF4444' : '#F59E0B'" class="mr-1">
                  {{ (vuln.criticality || 'warning') === 'critical' ? 'mdi-alert-circle' : 'mdi-alert' }}
                </v-icon>
                <span class="text-body-2 font-weight-bold">{{ vuln.skill || vuln.name }}</span>
                <v-spacer />
                <v-chip
                  x-small
                  :color="(vuln.criticality || 'warning') === 'critical' ? '#FEE2E2' : '#FEF3C7'"
                  :text-color="(vuln.criticality || 'warning') === 'critical' ? '#EF4444' : '#F59E0B'"
                >
                  {{ vuln.count }} {{ vuln.count === 1 ? 'person' : 'people' }}
                </v-chip>
              </div>
              <div v-if="vuln.holders && vuln.holders.length > 0" class="pl-4">
                <div v-for="h in vuln.holders" :key="h.profileId || h.name" class="text-caption grey--text">
                  {{ h.name }} <span v-if="h.title">- {{ h.title }}</span>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <v-card outlined class="pa-4 text-center" style="border: 1px dashed #90CAF9">
        <v-icon color="#007AFF" class="mb-2">mdi-account-arrow-right</v-icon>
        <div class="text-body-2 grey--text text--darken-1 mb-2">
          Want to analyze a specific person's skills against a target role?
        </div>
        <v-btn small color="#007AFF" dark @click="activeView = 'person'">
          Switch to Individual Analysis
        </v-btn>
      </v-card>
    </div>

    <!-- INDIVIDUAL PERSON ANALYSIS VIEW -->
    <div v-if="activeView === 'person'">
      <v-row>
        <v-col cols="12" md="5">
          <v-card outlined class="pa-4 mb-4">
            <h3 class="text-subtitle-2 font-weight-bold mb-3 theme-heading">
              Candidate & Target Role
            </h3>

            <v-autocomplete
              v-model="selectedProfile"
              :items="autocompleteResults"
              :loading="isSearching"
              :search-input.sync="searchText"
              item-text="name"
              item-value="profileId"
              return-object
              label="Search for a candidate..."
              outlined
              dense
              hide-no-data
              color="#007AFF"
              class="mb-3"
              prepend-inner-icon="mdi-account-search"
              @focus="searchProfiles('')"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.currentTitle }} at {{ item.currentCompany }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>

            <div v-if="profileName" class="mb-3 pa-2 selected-profile-highlight">
              <v-icon small color="#007AFF" class="mr-1">mdi-account-check</v-icon>
              <span class="text-body-2 font-weight-medium theme-heading">{{ profileName }}</span>
            </div>

            <v-textarea
              v-model="jobDescription"
              label="Paste a job description to analyze skills against..."
              outlined
              rows="6"
              color="#007AFF"
              hide-details
              class="mb-3"
              placeholder="Paste a full job description here to extract the required skills automatically..."
            />

            <v-text-field
              v-model="targetRole"
              label="Or enter a role title..."
              outlined
              dense
              color="#007AFF"
              class="mb-3"
              hint="Use a JD above for best results, or type a role title"
              persistent-hint
            />

            <v-btn
              block
              color="#007AFF"
              dark
              :loading="isLoading"
              :disabled="!profileId || (!targetRole && !jobDescription)"
              @click="analyze"
            >
              <v-icon left>mdi-chart-box</v-icon>
              Analyze Skills Gap
            </v-btn>
          </v-card>
        </v-col>

        <v-col cols="12" md="7">
          <div v-if="!hasResults && !isLoading">
            <v-card outlined class="pa-6 text-center mb-4">
              <v-icon size="64" color="#E0E0E0" class="mb-3">mdi-chart-box-outline</v-icon>
              <h3 class="text-subtitle-1 font-weight-bold mb-2 theme-heading">
                How Skills Gap Works
              </h3>
              <div class="text-body-2 grey--text text--darken-1 mb-4" style="max-width: 420px; margin: 0 auto">
                Our deterministic engine compares a candidate's verified skills against
                what top performers in the target role actually have — showing exactly
                where they match and where they need development.
              </div>
            </v-card>
          </div>

          <div v-if="hasResults">
            <v-row dense class="mb-4">
              <v-col cols="4">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h4 font-weight-bold" :style="{ color: readinessColor }">
                    {{ readinessScore }}%
                  </div>
                  <div class="text-caption grey--text">Readiness Score</div>
                </v-card>
              </v-col>
              <v-col cols="4">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h4 font-weight-bold" style="color: #10B981">
                    {{ matchedSkills.length }}
                  </div>
                  <div class="text-caption grey--text">Matched Skills</div>
                </v-card>
              </v-col>
              <v-col cols="4">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h4 font-weight-bold" style="color: #8B5CF6">
                    {{ gapSkills.length }}
                  </div>
                  <div class="text-caption grey--text">Growth Areas</div>
                </v-card>
              </v-col>
            </v-row>

            <v-card outlined class="pa-4 mb-4">
              <h3 class="text-subtitle-2 font-weight-bold mb-2 theme-heading">
                {{ profileName }} → {{ targetRole }}
              </h3>

              <v-progress-linear
                :value="readinessScore"
                :color="readinessColor"
                height="10"
                rounded
                class="mb-4"
              />

              <!-- Detailed skill breakdown if available -->
              <div v-if="skillDetails.length > 0" class="mb-4">
                <div class="text-caption grey--text mb-2">Skill-by-Skill Breakdown</div>
                <div v-for="sd in skillDetails" :key="sd.name" class="mb-2">
                  <div class="d-flex align-center justify-space-between mb-1">
                    <div class="d-flex align-center">
                      <span class="text-body-2 font-weight-medium">{{ sd.name }}</span>
                      <v-chip
                        x-small
                        class="ml-2"
                        :color="sd.weight === 'critical' ? '#FEE2E2' : sd.weight === 'high' ? '#FEF3C7' : '#F3F4F6'"
                        :text-color="sd.weight === 'critical' ? '#EF4444' : sd.weight === 'high' ? '#F59E0B' : '#6B7280'"
                      >
                        {{ sd.weight }}
                      </v-chip>
                    </div>
                    <span class="text-body-2 font-weight-bold" :style="{ color: skillScoreColor(sd.score) }">
                      {{ sd.score }}%
                    </span>
                  </div>
                  <v-progress-linear
                    :value="sd.score"
                    :color="skillScoreColor(sd.score)"
                    height="6"
                    rounded
                    class="mb-1"
                  />
                  <div class="text-caption grey--text" v-if="sd.evidence">
                    {{ sd.evidence }}
                  </div>
                </div>
              </div>

              <!-- Simple matched/gap chips if no details -->
              <div v-else>
                <div class="mb-3" v-if="matchedSkills.length > 0">
                  <div class="text-caption grey--text mb-1">Strong Matches</div>
                  <v-chip
                    v-for="skill in matchedSkills"
                    :key="skill"
                    small
                    color="#E8F5E9"
                    text-color="#10B981"
                    class="mr-1 mb-1"
                  >
                    <v-icon x-small left>mdi-check</v-icon>
                    {{ skill }}
                  </v-chip>
                </div>

                <div class="mb-3" v-if="gapSkills.length > 0">
                  <div class="text-caption grey--text mb-1">Growth Opportunities</div>
                  <v-chip
                    v-for="skill in gapSkills"
                    :key="skill"
                    small
                    outlined
                    color="#8B5CF6"
                    class="mr-1 mb-1"
                  >
                    <v-icon x-small left>mdi-arrow-up-circle-outline</v-icon>
                    {{ skill }}
                  </v-chip>
                </div>
              </div>

              <v-alert
                dense
                text
                :type="readinessScore >= 70 ? 'success' : 'info'"
                :color="readinessScore >= 70 ? undefined : readinessScore >= 40 ? '#F59E0B' : '#8B5CF6'"
                class="mt-3"
              >
                {{ recommendation }}
              </v-alert>
            </v-card>
          </div>

          <v-progress-linear v-if="isLoading" indeterminate color="#007AFF" />
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  name: "IntelligenceSkillsGap",
  data() {
    return {
      searchText: "",
    };
  },
  computed: {
    ...mapState("intelligenceSkillsGap", [
      "profileId",
      "profileName",
      "readinessScore",
      "matchedSkills",
      "gapSkills",
      "skillDetails",
      "recommendation",
      "autocompleteResults",
      "isLoading",
      "isSearching",
      "isLoadingHeatMap",
      "hasError",
      "errorMessage",
    ]),
    ...mapGetters("intelligenceSkillsGap", [
      "hasResults",
      "topOrgSkills",
      "rarestSkills",
      "heatMapRows",
      "heatMapAreas",
      "heatMapSeniorities",
      "seniorityRows",
      "areaConcentration",
      "heatMapSummary",
    ]),
    activeView: {
      get() { return this.$store.state.intelligenceSkillsGap.activeView; },
      set(val) { this.$store.commit("intelligenceSkillsGap/setActiveView", val); },
    },
    selectedProfile: {
      get() { return this.profileId ? { profileId: this.profileId, name: this.profileName } : null; },
      set(val) { if (val) this.$store.commit("intelligenceSkillsGap/selectProfile", val); },
    },
    targetRole: {
      get() { return this.$store.state.intelligenceSkillsGap.targetRole; },
      set(val) { this.$store.commit("intelligenceSkillsGap/updateField", { path: "targetRole", value: val }); },
    },
    useCustomJD: {
      get() { return this.$store.state.intelligenceSkillsGap.useCustomJD; },
      set(val) { this.$store.commit("intelligenceSkillsGap/updateField", { path: "useCustomJD", value: val }); },
    },
    jobDescription: {
      get() { return this.$store.state.intelligenceSkillsGap.jobDescription; },
      set(val) { this.$store.commit("intelligenceSkillsGap/updateField", { path: "jobDescription", value: val }); },
    },
    readinessColor() {
      if (this.readinessScore >= 70) return "#10B981";
      if (this.readinessScore >= 40) return "#F59E0B";
      return "#EF4444";
    },
  },
  watch: {
    searchText(val) {
      // Load on every change including empty so the dropdown is browsable
      // without needing to know a name in advance.
      this.searchProfiles(val || "");
    },
  },
  mounted() {
    this.loadHeatMap();
    const viewParam = this.$route.query.view;
    const profileId = this.$route.query.profileId;
    if (profileId) {
      this.$store.commit("intelligenceSkillsGap/updateField", { path: "profileId", value: profileId });
      this.activeView = "person";
    } else if (viewParam === "person") {
      this.activeView = "person";
    } else {
      this.activeView = "heatmap";
    }
  },
  methods: {
    ...mapActions("intelligenceSkillsGap", ["analyzeGap", "loadHeatMap", "searchProfiles"]),
    analyze() {
      this.analyzeGap();
    },
    areaShort(area) {
      const map = {
        "Data & Analytics": "Data",
        "Sales & GTM": "Sales",
        "People & HR": "HR",
      };
      return map[area] || area;
    },
    heatCellStyle(percentage) {
      if (percentage <= 0) return { background: "rgba(239,83,80,0.18)", color: "rgba(239,83,80,0.8)", border: "1px dashed rgba(239,83,80,0.45)" };
      if (percentage <= 5) return { background: "rgba(150,150,150,0.08)", color: "rgba(160,160,160,0.8)" };
      if (percentage <= 15) return { background: "rgba(0,122,255,0.1)", color: "rgba(0,122,255,0.7)" };
      if (percentage <= 25) return { background: "rgba(0,122,255,0.2)", color: "rgba(0,122,255,0.9)" };
      if (percentage <= 40) { const a = 0.25 + (percentage - 25) / 15 * 0.15; return { background: `rgba(0,122,255,${a})`, color: "rgba(0,122,255,0.9)" }; }
      if (percentage <= 60) { const a = 0.4 + (percentage - 40) / 20 * 0.2; return { background: `rgba(0,122,255,${a})`, color: "#FFFFFF" }; }
      if (percentage <= 80) { const a = 0.6 + (percentage - 60) / 20 * 0.15; return { background: `rgba(0,122,255,${a})`, color: "#FFFFFF" }; }
      const a = 0.75 + (percentage - 80) / 20 * 0.15; return { background: `rgba(0,122,255,${a})`, color: "#FFFFFF" };
    },
    skillScoreColor(score) {
      if (score >= 70) return "#10B981";
      if (score >= 40) return "#F59E0B";
      return "#EF4444";
    },
  },
};
</script>

<style scoped>
.intelligence-skills-gap {
  max-width: 1400px;
}

.heatmap-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.heatmap-table th,
.heatmap-table td {
  padding: 6px 8px;
  text-align: center;
  border: 1px solid rgba(255,255,255,0.08);
}

.heatmap-header-skill {
  text-align: left !important;
  min-width: 140px;
  font-weight: 600;
  white-space: nowrap;
}

.heatmap-header-total {
  min-width: 50px;
  font-weight: 600;
}

.heatmap-header-area {
  min-width: 60px;
  font-weight: 600;
  font-size: 11px;
  white-space: nowrap;
}

.heatmap-skill-name {
  text-align: left !important;
  font-weight: 500;
  white-space: nowrap;
}

.heatmap-total {
  font-size: 11px;
}

.heatmap-cell {
  min-width: 40px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 2px;
  transition: background 0.2s;
}

.heatmap-legend-block {
  width: 20px;
  height: 12px;
  border-radius: 2px;
  margin: 0 1px;
}

.vuln-card {
  border-radius: 6px;
  border: 1px solid;
}

.vuln-critical {
  border-color: rgba(239,68,68,0.3);
  background: rgba(239,68,68,0.05);
}

.vuln-warning {
  border-color: rgba(245,158,11,0.3);
  background: rgba(245,158,11,0.05);
}

.theme--light .heatmap-table th,
.theme--light .heatmap-table td {
  border-color: rgba(0,0,0,0.08);
}

.selected-profile-highlight {
  background: #E3F2FD;
  border-radius: 4px;
}

.theme--dark .selected-profile-highlight {
  background: rgba(0, 122, 255, 0.15);
}

.theme--dark .vuln-critical {
  background: rgba(239,68,68,0.12);
}

.theme--dark .vuln-warning {
  background: rgba(245,158,11,0.12);
}
</style>
